import { useMutation, useQuery } from '@apollo/client';
import React from 'react';
import { useParams } from 'react-router';
import Form, { Field, Submit } from '../../components/Form';
import { gql } from '../../graphql/generated/gql';
import Link from '../../utils/router/Link';
import AccountContentContainer from '../AccountContentContainer';
import usePostLoginHandler from '../usePostLoginHandler';
import getErrorMessage from './getErrorMessage';
import * as styles from './ResetView.module.scss';

// eslint-disable-next-line slab-graphql/no-unused-fields
const PASSWORD_TOKEN_QUERY = gql(/** @complexity 1 */ `
  query passwordToken($id: ID!, $type: String!) {
    passwordToken(id: $id, type: $type)
  }
`);

const RESET_PASSWORD_MUTATION = gql(/** @complexity 2 */ `
  mutation resetPassword($id: ID!, $password: String!) {
    session: resetPassword(id: $id, password: $password) {
      authToken
    }
  }
`);

type ResetPasswordValues = { password: string };

const ResetView = () => {
  const { name } = window.__slab.PUBLIC_ORG;
  const { token } = useParams() as { token: string };

  const { error } = useQuery(PASSWORD_TOKEN_QUERY, {
    variables: { id: token, type: 'reset' },
  });

  const [resetPassword] = useMutation(RESET_PASSWORD_MUTATION);

  const postLoginHandler = usePostLoginHandler();

  const onSubmit = (values: ResetPasswordValues) =>
    resetPassword({ variables: { id: token, password: values.password } }).then(
      ({ data }) => {
        if (data?.session) {
          postLoginHandler(data.session.authToken);
        }
      },
    );

  const errorMessage = getErrorMessage(error, 'reset password link');

  return (
    <AccountContentContainer>
      {errorMessage ? (
        <>
          <div className={styles.errorMessage}>{errorMessage}</div>
          <p>
            Please try again with <Link to="/forgot">a new reset link</Link>.
          </p>
        </>
      ) : (
        <>
          <h2>Password Reset</h2>
          <p>
            Enter a new password for your <strong>{name}</strong> Slab account.
          </p>
          <Form<ResetPasswordValues>
            initialValues={{ password: '' }}
            onSubmit={onSubmit}
          >
            <Field
              required
              placeholder="Your new password"
              type="password"
              name="password"
              margin="compact"
            />
            <Submit block>Change Password</Submit>
          </Form>
        </>
      )}
    </AccountContentContainer>
  );
};

export default ResetView;
